import dayjs from "dayjs";
import { useIntl } from "react-intl";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import { activityStore } from "../../../../../store/activity.store";
import { Activity, Interval } from "../../../../../model/activity.model";
import { ActivityStatus } from "../../../../../enum/activity.enum";
import { getActivityTypeOptions } from "../../../../AuthoredProvider/components/AuthoredHeader/helper";
import Intervals from "./components/Intervals";
import style from "./style.module.css";
import { debounce } from "../../../../../store/utils/debounce";
import { TreeSelect } from "../../../../ui/tree-select";
import { Segmented } from "../../../../ui/segmented";
import { Label } from "../../../../ui/label";
import { Textarea } from "../../../../ui/textarea";
import { TagInput } from "../../../../ui/tag-input";
import { DateTimePicker } from "../../../../ui/datetime-picker";

interface ActivityFormValues {
  status: ActivityStatus;
  typeId: string;
  start?: string;
  comment: string;
  tags: string[];
  intervals: Interval[];
}

export interface ActivityFormRef {
  submit: () => Promise<void>;
}

interface IActivityForm {
  activity: Partial<Activity>;
  onSubmit?: (values: ActivityFormValues) => void;
}

const CreateActivityForm = forwardRef<ActivityFormRef, IActivityForm>(({ activity, onSubmit }, ref) => {
  const { formatMessage } = useIntl();
  const availableActivityTags = activityStore(state => state.activityTags);
  const activitiesTypesLoading = activityStore(state => state.loading.getActivityTypes);
  const activityTypes = activityStore(state => state.activityTypes || []);
  const createActivity = activityStore(state => state.createActivity);

  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [isStartFieldDisabled, setIsStartFieldDisabled] = useState<boolean>();

  // Track the current occurrence time for occurrence types
  const [occurrenceTime, setOccurrenceTime] = useState<string>(new Date().toISOString());

  const getTagsStartPage = activityStore(state => state.getActivityTagsStartPage);
  const tagsLoading = activityStore(state => state.loading.getActivityTagsStartPage);
  const setActivityTagsParams = activityStore(state => state.setActivityTagsParams);
  const query = activityStore(state => state.activityTagsParams.query);

  const { control, handleSubmit, setValue, watch, reset } = useForm<ActivityFormValues>({
    defaultValues: {
      status: activity.status,
      typeId: activity.typeId || '',
      start: activity.start,
      comment: activity.comment || '',
      tags: activity.tags || [],
      intervals: activity.intervals || [],
    }
  });

  // Watch the typeId to dynamically determine if it's an occurrence
  const watchedTypeId = watch('typeId');

  // Determine if the selected type is an occurrence
  const isOccurrence = useMemo(() => {
    const type = activityTypes.find(t => t.id === watchedTypeId);
    return !!type?.occurrence;
  }, [activityTypes, watchedTypeId]);

  // Expose submit method via ref
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(handleFormSubmit)()
  }));

  useEffect(() => {
    !tagsLoading && getTagsStartPage();
  }, [query]);

  useEffect(() => {
    reset({
      status: activity.status,
      typeId: activity.typeId || '',
      start: activity.start,
      comment: activity.comment || '',
      tags: activity.tags || [],
      intervals: activity.intervals || [],
    });

    setIsStartFieldDisabled(activity.status !== ActivityStatus.RUNNING);
    activity.intervals && setIntervals(activity.intervals);
  }, [activity, reset]);

  // When switching to occurrence type, set status to STOPPED and clear intervals
  useEffect(() => {
    if (isOccurrence) {
      setValue('status', ActivityStatus.STOPPED);
      setIsStartFieldDisabled(true);
    }
  }, [isOccurrence, setValue]);

  const handleFormSubmit = useCallback((values: ActivityFormValues) => {
    // For occurrence types, create a single interval with start === finish
    if (isOccurrence) {
      const occInterval: Interval = {
        id: '',
        typeId: values.typeId,
        activityId: '',
        tags: values.tags,
        start: occurrenceTime,
        finish: occurrenceTime,
        comment: values.comment,
      };
      values.intervals = [occInterval];
      values.status = ActivityStatus.STOPPED;
    }

    const submitFn = onSubmit || createActivity;
    return submitFn(values);
  }, [onSubmit, createActivity, isOccurrence, occurrenceTime]);

  const onSearchCallback = useCallback(debounce((value: string) =>
    setActivityTagsParams({ query: value }), 500), [setActivityTagsParams]
  );

  const filterTreeNodeCallback = useCallback((inputValue: string, treeNode: any) => !inputValue
    ? true
    : treeNode.name.toLowerCase().includes(inputValue.toLowerCase()
    ), []);

  const onRadioChangeCallback = useCallback((value: string) => {
    const isActivityStatusPausedOrStopped = activity.status === ActivityStatus.STOPPED || activity.status === ActivityStatus.PAUSED;
    const isFormStatusPausedOrStopped = value === ActivityStatus.PAUSED || value === ActivityStatus.STOPPED;

    setIsStartFieldDisabled(isFormStatusPausedOrStopped && activity.status === ActivityStatus.RUNNING);

    if (isActivityStatusPausedOrStopped && value === ActivityStatus.RUNNING) {
      setValue('start', new Date().toISOString());
    }

    if (isActivityStatusPausedOrStopped && isFormStatusPausedOrStopped) {
      setIsStartFieldDisabled(true);
      setValue('start', undefined);
    }
  }, [activity, setValue]);

  return (
    <form className="space-y-4">
      {/* Status - only for non-occurrence types */}
      {!isOccurrence && (
        <div className="grid grid-cols-[100px_1fr] items-center gap-4">
          <Label>{formatMessage({ id: 'STATUS' })}</Label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Segmented
                className="w-fit"
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  onRadioChangeCallback(value as string);
                }}
                options={[
                  { value: ActivityStatus.PAUSED, label: formatMessage({ id: ActivityStatus.PAUSED }) },
                  { value: ActivityStatus.RUNNING, label: formatMessage({ id: ActivityStatus.RUNNING }) },
                  { value: ActivityStatus.STOPPED, label: formatMessage({ id: ActivityStatus.STOPPED }) },
                ]}
              />
            )}
          />
        </div>
      )}

      {/* Type selector - shows all types */}
      <div className="grid grid-cols-[100px_1fr] items-center gap-4">
        <Label>{formatMessage({ id: 'TYPE_ID' })}</Label>
        <Controller
          name="typeId"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TreeSelect
              className={style.select}
              filterTreeNode={filterTreeNodeCallback}
              loading={activitiesTypesLoading}
              showSearch
              onChange={(value) => field.onChange(Array.isArray(value) ? value[0] : value)}
              value={field.value}
              treeData={getActivityTypeOptions(activityTypes)}
              placeholder={formatMessage({ id: 'SELECT_ACTIVITY' })}
            />
          )}
        />
      </div>

      {/* Comment */}
      <div className="grid grid-cols-[100px_1fr] items-start gap-4">
        <Label className="pt-1.5">{formatMessage({ id: 'COMMENT' })}</Label>
        <Controller
          name="comment"
          control={control}
          render={({ field }) => <Textarea {...field} />}
        />
      </div>

      {/* For occurrence: single Time picker */}
      {isOccurrence ? (
        <div className="grid grid-cols-[100px_1fr] items-center gap-4">
          <Label>{formatMessage({ id: 'TIME' })}</Label>
          <DateTimePicker
            showTime
            value={dayjs(occurrenceTime)}
            onChange={(date) => {
              if (date) {
                setOccurrenceTime(date.toISOString());
              }
            }}
          />
        </div>
      ) : (
        <>
          {/* Start field - only for running non-occurrence activities */}
          {!isStartFieldDisabled && (
            <div className="grid grid-cols-[100px_1fr] items-center gap-4">
              <Label>{formatMessage({ id: 'START' })}</Label>
              <Controller
                name="start"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    disabled={isStartFieldDisabled}
                    showTime
                    value={field.value ? dayjs(field.value) : undefined}
                    onChange={(date) => field.onChange(date?.toISOString())}
                  />
                )}
              />
            </div>
          )}
        </>
      )}

      {/* Tags */}
      <div className="grid grid-cols-[100px_1fr] items-center gap-4">
        <Label>{formatMessage({ id: 'TAGS' })}</Label>
        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <TagInput
              allowClear
              showSearch
              value={field.value}
              onChange={field.onChange}
              onSearch={onSearchCallback}
              options={availableActivityTags?.map(tag => ({ value: tag, label: tag }))}
              loading={activitiesTypesLoading}
              placeholder={formatMessage({ id: 'SELECT_TAGS' })}
            />
          )}
        />
      </div>

      {/* Intervals - only for non-occurrence types */}
      {!isOccurrence && (
        <Intervals
          activity={activity as Activity}
          intervals={intervals}
          setValue={setValue}
          watch={watch}
          setIntervals={setIntervals}
        />
      )}
    </form>
  );
});

CreateActivityForm.displayName = 'CreateActivityForm';

export default CreateActivityForm;
